import type { Folder } from "@models";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemButton,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { compareAlphabetically } from "src/utils/sorting";

import type { DocumentsPageItem } from "../Documents.utils";
import type { DocumentsTableMoveTarget } from "../DocumentsTable.types";

type DocumentsTableMoveDialogProps = {
  folders: Folder[];
  item: DocumentsPageItem | null;
  onClose: () => void;
  onMove: (target: DocumentsTableMoveTarget) => void;
};

const sortFolders = (folders: Folder[]) =>
  [...folders].sort((left, right) => {
    const byName = compareAlphabetically(left.name, right.name);
    return byName !== 0 ? byName : compareAlphabetically(left.id, right.id);
  });

const getOrderedFolderRows = (folders: Folder[]) => {
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));
  const childFoldersByParent = new Map<string, Folder[]>();
  const topLevelFolders: Folder[] = [];

  folders.forEach((folder) => {
    const parentFolderId = folder.parentFolderId ?? null;

    if (!parentFolderId || !foldersById.has(parentFolderId)) {
      topLevelFolders.push(folder);
      return;
    }

    const existingChildren = childFoldersByParent.get(parentFolderId) ?? [];
    existingChildren.push(folder);
    childFoldersByParent.set(parentFolderId, existingChildren);
  });

  const rows: Array<{ depth: number; folder: Folder }> = [];
  const visited = new Set<string>();

  const appendFolder = (folder: Folder, depth: number, path: Set<string>) => {
    if (visited.has(folder.id) || path.has(folder.id)) {
      return;
    }

    visited.add(folder.id);
    rows.push({ depth, folder });

    const nextPath = new Set(path);
    nextPath.add(folder.id);

    sortFolders(childFoldersByParent.get(folder.id) ?? []).forEach(
      (childFolder) => appendFolder(childFolder, depth + 1, nextPath)
    );
  };

  sortFolders(topLevelFolders).forEach((folder) =>
    appendFolder(folder, 0, new Set<string>())
  );
  sortFolders(folders.filter((folder) => !visited.has(folder.id))).forEach(
    (folder) => appendFolder(folder, 0, new Set<string>())
  );

  return rows;
};

const isFolderInsideFolder = ({
  ancestorFolderId,
  folderId,
  foldersById,
}: {
  ancestorFolderId: string;
  folderId: string;
  foldersById: Map<string, Folder>;
}) => {
  let currentFolderId: string | null = folderId;

  while (currentFolderId) {
    if (currentFolderId === ancestorFolderId) {
      return true;
    }

    currentFolderId = foldersById.get(currentFolderId)?.parentFolderId ?? null;
  }

  return false;
};

export const DocumentsTableMoveDialog = ({
  folders,
  item,
  onClose,
  onMove,
}: DocumentsTableMoveDialogProps) => {
  const [selectedTargetFolderId, setSelectedTargetFolderId] = useState<
    string | null | undefined
  >(undefined);
  const orderedFolderRows = useMemo(
    () => getOrderedFolderRows(folders),
    [folders]
  );
  const foldersById = useMemo(
    () => new Map(folders.map((folder) => [folder.id, folder])),
    [folders]
  );
  const availableFolderRows = useMemo(() => {
    if (item?.kind !== "folder") {
      return orderedFolderRows;
    }

    return orderedFolderRows.filter(
      ({ folder }) =>
        !isFolderInsideFolder({
          ancestorFolderId: item.id,
          folderId: folder.id,
          foldersById,
        })
    );
  }, [foldersById, item, orderedFolderRows]);
  const isOpen = item !== null;
  const itemKindLabel = item?.kind ?? "item";

  const handleClose = () => {
    setSelectedTargetFolderId(undefined);
    onClose();
  };

  const handleMove = () => {
    if (selectedTargetFolderId === undefined) {
      return;
    }

    onMove(
      selectedTargetFolderId === null
        ? { type: "root" }
        : { type: "folder", folderId: selectedTargetFolderId }
    );
    handleClose();
  };

  return (
    <Dialog
      aria-labelledby="documents-table-move-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={isOpen}
    >
      <DialogTitle id="documents-table-move-dialog-title">
        Move {itemKindLabel}
      </DialogTitle>
      <DialogContent>
        <Typography className="mb-3" variant="body2">
          {item
            ? `Choose a destination for ${item.name}.`
            : "Choose a destination for this item."}
        </Typography>
        <ListItemButton
          component="button"
          data-testid="documents-table-move-root-target"
          onClick={() => setSelectedTargetFolderId(null)}
          selected={selectedTargetFolderId === null}
          sx={{
            borderRadius: 1,
            marginBottom: 0.5,
            paddingY: 0.75,
            textAlign: "left",
            width: "100%",
          }}
          type="button"
        >
          Root
        </ListItemButton>
        <div className="max-h-80 overflow-y-auto">
          {availableFolderRows.map(({ depth, folder }) => (
            <ListItemButton
              component="button"
              data-testid={`documents-table-move-folder-target-${folder.id}`}
              key={folder.id}
              onClick={() => setSelectedTargetFolderId(folder.id)}
              selected={selectedTargetFolderId === folder.id}
              sx={{
                borderRadius: 1,
                marginBottom: 0.5,
                paddingLeft: 2 + depth * 2,
                paddingY: 0.75,
                textAlign: "left",
                width: "100%",
              }}
              type="button"
            >
              {folder.name}
            </ListItemButton>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          disabled={selectedTargetFolderId === undefined}
          onClick={handleMove}
          variant="contained"
        >
          Move
        </Button>
      </DialogActions>
    </Dialog>
  );
};
